import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  FileText
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useBrand } from '@/hooks/useBrand'

interface PlatformSetupGuidesProps {
  webhookUrl?: string
}

interface PlatformGuide {
  id: string
  name: string
  icon: React.ReactNode
  description: string
  difficulty: 'Easy' | 'Medium'
  pluginRequired?: string
  steps: Array<{
    title: string
    description: string
    code?: string
    note?: string
    link?: { text: string; url: string }
  }>
  fieldMappingTips?: string[]
}

const getPlatforms = (appName: string): PlatformGuide[] => [
  {
    id: 'cf7',
    name: 'Contact Form 7',
    icon: (
      <div className="w-10 h-10 bg-[#0073aa] rounded-lg flex items-center justify-center text-white font-bold text-sm">
        CF7
      </div>
    ),
    description: 'Most popular free WordPress form plugin',
    difficulty: 'Easy',
    pluginRequired: 'Contact Form 7 + Contact Form to Any API',
    steps: [
      {
        title: 'Install the plugins',
        description: 'Install "Contact Form 7" and "Contact Form to Any API" plugins from the WordPress plugin repository.',
        link: {
          text: 'Contact Form to Any API Plugin',
          url: 'https://wordpress.org/plugins/contact-form-to-any-api/'
        }
      },
      {
        title: 'Create or edit your form',
        description: 'Go to Contact → Contact Forms in your WordPress admin. Create a new form or edit an existing one.'
      },
      {
        title: 'Configure the API connection',
        description: 'Click on the "API Integration" tab in your form settings. Select "POST" as the method and paste your webhook URL.',
        note: 'Make sure to select "JSON" as the format for the best compatibility.'
      },
      {
        title: 'Map your form fields',
        description: 'Map your form fields to the API. Use the field names from your CF7 form (e.g., your-name, your-email, your-phone, your-message).',
        code: `{
  "name": "[your-name]",
  "email": "[your-email]",
  "phone": "[your-phone]",
  "message": "[your-message]"
}`
      },
      {
        title: 'Test the integration',
        description: `Submit a test form entry. Return to ${appName} and check if the webhook received the data in the "Listening" mode.`
      }
    ],
    fieldMappingTips: [
      'CF7 uses field names like "your-name", "your-email", "your-phone" by default',
      'You can customize field names in the CF7 form editor',
      'The phone field is required for AI call triggers'
    ]
  },
  {
    id: 'gravityforms',
    name: 'Gravity Forms',
    icon: (
      <div className="w-10 h-10 bg-[#f15a29] rounded-lg flex items-center justify-center text-white font-bold text-sm">
        GF
      </div>
    ),
    description: 'Premium WordPress form builder with native webhook support',
    difficulty: 'Easy',
    pluginRequired: 'Gravity Forms + Webhooks Add-On',
    steps: [
      {
        title: 'Enable the Webhooks Add-On',
        description: 'Go to Forms → Add-Ons in your WordPress admin. Find "Webhooks" and click "Activate". If not installed, download it from your Gravity Forms account.',
        link: {
          text: 'Gravity Forms Webhooks Documentation',
          url: 'https://docs.gravityforms.com/webhooks-add-on/'
        }
      },
      {
        title: 'Open your form settings',
        description: 'Edit your form and go to Settings → Webhooks. Click "Add New" to create a webhook feed.'
      },
      {
        title: 'Configure the webhook',
        description: `Give it a name like "${appName} Lead Webhook". Paste your webhook URL in the "Request URL" field. Set Request Method to "POST" and Request Format to "JSON".`
      },
      {
        title: 'Map the fields',
        description: 'In the "Request Body" section, select "Select Fields" and map your form fields. Make sure to include the phone number field.',
        note: 'Use the field IDs or labels that match your form. Gravity Forms will show you the available fields.'
      },
      {
        title: 'Enable and test',
        description: `Make sure the webhook is "Active". Submit a test entry and check if ${appName} receives it.`
      }
    ],
    fieldMappingTips: [
      'Gravity Forms uses numeric field IDs (e.g., "1", "2", "3") or custom labels',
      'You can use the "Admin Field Label" in Advanced settings for cleaner field names',
      'Enable "Request Headers" logging temporarily for debugging'
    ]
  },
  {
    id: 'wpforms',
    name: 'WPForms',
    icon: (
      <div className="w-10 h-10 bg-[#e27730] rounded-lg flex items-center justify-center text-white font-bold text-sm">
        WPF
      </div>
    ),
    description: 'Beginner-friendly WordPress form builder',
    difficulty: 'Medium',
    pluginRequired: 'WPForms + Zapier (or Webhooks addon for Pro)',
    steps: [
      {
        title: 'Choose your integration method',
        description: 'WPForms Pro users can use the Webhooks addon directly. WPForms Lite users can connect via Zapier for free.',
        link: {
          text: 'WPForms Webhooks Documentation',
          url: 'https://wpforms.com/docs/how-to-send-form-data-to-webhooks/'
        }
      },
      {
        title: 'Option A: Using Zapier (Free method)',
        description: 'Install the "Uncanny Automator" or "WPForms Zapier" plugin. Create a Zap with WPForms as the trigger and "Webhooks by Zapier" as the action.'
      },
      {
        title: 'Option B: Using WPForms Webhooks (Pro)',
        description: `Go to WPForms → Settings → Integrations → Webhooks. Add a new webhook with your ${appName} webhook URL.`
      },
      {
        title: 'Configure field mapping',
        description: 'Map your form fields to send name, email, phone, and message. The phone field is required for triggering calls.',
        code: `Zapier field mapping example:
- Name: {{wpforms.field_id.name}}
- Email: {{wpforms.field_id.email}}
- Phone: {{wpforms.field_id.phone}}
- Message: {{wpforms.field_id.message}}`
      },
      {
        title: 'Test the connection',
        description: `Submit a test form and verify the data appears in ${appName}.`
      }
    ],
    fieldMappingTips: [
      'WPForms uses field IDs like "field_id" format',
      'Find field IDs by editing the form and hovering over each field',
      'Zapier adds extra wrapper fields - check the sample payload carefully'
    ]
  },
  {
    id: 'wix',
    name: 'Wix Forms',
    icon: (
      <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white font-bold text-xs">
        Wix
      </div>
    ),
    description: 'Built-in forms on Wix websites',
    difficulty: 'Medium',
    pluginRequired: 'Wix Automations or Zapier',
    steps: [
      {
        title: 'Access Wix Automations',
        description: 'From your Wix Dashboard, go to Automations. Click "New Automation" to create a workflow.',
        link: {
          text: 'Wix Automations Guide',
          url: 'https://support.wix.com/en/article/creating-an-automation'
        }
      },
      {
        title: 'Set the trigger',
        description: 'Choose "Wix Forms" as the trigger and select "Form submitted". Pick the form you want to connect.'
      },
      {
        title: 'Add a webhook action',
        description: `For the action, choose "Send an HTTP request" (or use Zapier if preferred). Enter your ${appName} webhook URL and select POST method.`
      },
      {
        title: 'Configure the payload',
        description: 'Set the content type to "application/json" and build your JSON payload using the dynamic field values from your form.',
        code: `{
  "name": "{{contact.name}}",
  "email": "{{contact.email}}",
  "phone": "{{form.phone}}",
  "message": "{{form.message}}"
}`
      },
      {
        title: 'Activate and test',
        description: 'Save and activate the automation. Submit a test form entry to verify the connection.'
      }
    ],
    fieldMappingTips: [
      'Wix uses dynamic variables like {{contact.name}} for standard fields',
      'Custom form fields use {{form.fieldName}} format',
      'Test with the automation preview before going live'
    ]
  },
  {
    id: 'squarespace',
    name: 'Squarespace Forms',
    icon: (
      <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-white font-bold text-xs">
        SQ
      </div>
    ),
    description: 'Native Squarespace form blocks',
    difficulty: 'Medium',
    pluginRequired: 'Zapier or Make integration',
    steps: [
      {
        title: 'Connect Squarespace to Zapier',
        description: 'You\'ll need Zapier (or Make/Integromat) to send Squarespace form data to webhooks since Squarespace doesn\'t have native webhook support.',
        link: {
          text: 'Squarespace Zapier Integration',
          url: 'https://zapier.com/apps/squarespace/integrations'
        }
      },
      {
        title: 'Create a new Zap',
        description: 'In Zapier, create a new Zap with "Squarespace" as the trigger app. Choose "New Form Submission" as the trigger event.'
      },
      {
        title: 'Connect your Squarespace account',
        description: 'Follow the prompts to connect your Squarespace account and select the form you want to monitor.'
      },
      {
        title: 'Add a Webhook action',
        description: `Add "Webhooks by Zapier" as the action. Choose "POST" and enter your ${appName} webhook URL. Set the payload type to JSON.`,
        code: `Map fields in Zapier:
- name: [Form Response Name field]
- email: [Form Response Email field]
- phone: [Form Response Phone field]
- message: [Form Response Message field]`
      },
      {
        title: 'Test and enable',
        description: 'Use Zapier\'s test feature to send a sample submission. Once working, turn on your Zap.'
      }
    ],
    fieldMappingTips: [
      'Squarespace forms use generic field names in Zapier',
      'Phone fields may appear as "Phone Number" or custom label',
      'Check the Zapier test data to see exact field names'
    ]
  },
  {
    id: 'jotform',
    name: 'Jotform',
    icon: (
      <div className="w-10 h-10 bg-[#ff6100] rounded-lg flex items-center justify-center text-white font-bold text-sm">
        JF
      </div>
    ),
    description: 'Popular standalone form builder with native webhooks',
    difficulty: 'Easy',
    steps: [
      {
        title: 'Open form settings',
        description: 'In Jotform, open your form and go to Settings → Integrations. Search for "Webhook" and click on it.',
        link: {
          text: 'Jotform Webhook Integration',
          url: 'https://www.jotform.com/help/245-how-to-setup-a-webhook'
        }
      },
      {
        title: 'Add your webhook URL',
        description: `Paste your ${appName} webhook URL in the "Webhook URL" field. Click "Complete Integration".`
      },
      {
        title: 'Submit a test entry',
        description: 'Go back to your form and submit a test entry. Jotform will automatically send all form fields to the webhook.'
      },
      {
        title: `Map fields in ${appName}`,
        description: `Return to ${appName} and complete the field mapping wizard. Jotform sends all fields with their question labels as keys.`
      }
    ],
    fieldMappingTips: [
      'Jotform uses question labels as field names (e.g., "Full Name", "Email Address")',
      'Field names may include "q1_", "q2_" prefixes for question IDs',
      'Check the sample payload to find exact field names'
    ]
  }
]

export function PlatformSetupGuides({ webhookUrl }: PlatformSetupGuidesProps) {
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const { toast } = useToast()
  const brand = useBrand()
  const platforms = getPlatforms(brand.appName)

  const copyWebhookUrl = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl)
      setCopiedUrl(true)
      toast({ title: 'Copied', description: 'Webhook URL copied to clipboard' })
      setTimeout(() => setCopiedUrl(false), 2000)
    }
  }

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast({ title: 'Copied', description: 'Code snippet copied to clipboard' })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Platform Setup Guides
        </CardTitle>
        <CardDescription>
          Step-by-step instructions to connect your website forms to {brand.appName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Webhook URL Quick Copy */}
        {webhookUrl && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium mb-2">Your Webhook URL</p>
            <div className="flex gap-2">
              <code className="flex-1 bg-background px-3 py-2 rounded border text-sm font-mono truncate">
                {webhookUrl}
              </code>
              <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
                {copiedUrl ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Copy this URL and paste it into your form platform's webhook settings
            </p>
          </div>
        )}

        {/* Platform Cards */}
        <div className="space-y-3">
          {platforms.map((platform) => (
            <Collapsible
              key={platform.id}
              open={expandedPlatform === platform.id}
              onOpenChange={(open) => setExpandedPlatform(open ? platform.id : null)}
            >
              <div className="border rounded-lg">
                <CollapsibleTrigger asChild>
                  <button className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg">
                    <div className="flex items-center gap-3">
                      {platform.icon}
                      <div className="text-left">
                        <h3 className="font-medium">{platform.name}</h3>
                        <p className="text-sm text-muted-foreground">{platform.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={platform.difficulty === 'Easy' ? 'default' : 'secondary'}>
                        {platform.difficulty}
                      </Badge>
                      {expandedPlatform === platform.id ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="px-4 pb-4 pt-2 border-t">
                    {platform.pluginRequired && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                        <p className="text-sm">
                          <span className="font-medium">Required: </span>
                          {platform.pluginRequired}
                        </p>
                      </div>
                    )}

                    <ol className="space-y-4">
                      {platform.steps.map((step, index) => (
                        <li key={index} className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm">{step.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {step.description}
                            </p>
                            {step.code && (
                              <div className="mt-2 relative">
                                <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
                                  <code>{step.code}</code>
                                </pre>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="absolute top-1 right-1 h-6 w-6 p-0"
                                  onClick={() => copyCode(step.code!)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            {step.note && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-start gap-1">
                                <span className="font-medium">Note:</span> {step.note}
                              </p>
                            )}
                            {step.link && (
                              <a
                                href={step.link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                              >
                                {step.link.text}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>

                    {platform.fieldMappingTips && platform.fieldMappingTips.length > 0 && (
                      <div className="mt-4 p-3 bg-muted rounded-lg">
                        <h4 className="text-sm font-medium mb-2">Field Mapping Tips</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {platform.fieldMappingTips.map((tip, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* General Tips */}
        <div className="mt-6 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg">
          <h4 className="font-medium mb-2">General Tips</h4>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">1.</span>
              Always send a <strong>phone number</strong> field - it's required to trigger AI calls
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">2.</span>
              Use {brand.appName}'s "Listening" mode to auto-detect your form's field names
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">3.</span>
              Test with your own phone number first before going live
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">4.</span>
              Check the webhook logs if calls aren't triggering - the payload will show what was received
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}
